import React, { useState, useEffect, useRef } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet';
import * as yup from 'yup';

import Response from './Response';

import SubmitButton from './global/SubmitButton';
import Input from './global/Input';
import Breadcrumb from './global/Breadcrumb';
import GridContainer from './global/GridContainer';
import Fieldset from './global/Fieldset';
import TransactionField from './global/TransactionField';

import H1 from './../typography/H1';
import Legend from './../typography/Legend';

import Captcha from '../captcha/CaptchaWrapper';

import { SITE_KEY } from '../const/Const';

import { trackPromise } from '../promise-tracker';

import useYupValidationResolver from '../hooks/useYupValidationResolver';

const cardTopupValidationSchema = yup.object().shape({
  ordPhoneNumber: yup
    .string()
    .required()
    .matches(/^(\([0-9]|[0-9])(\d{2}|\d{2}\))(-|\.|\s)?\d{3}(-|\.|\s)?\d{4}$/)
    .when('$state', (state, schema) =>
      state !== undefined
        ? schema.test('custom-is-prepaid', 'Not a valid prepaid number', number => {
            const transformedNumber = number.replace(/[^0-9]/g, '');
            if (transformedNumber.length === 0) {
              return new Promise(resolve => resolve(false));
            } else if (state.prepaidNumberValidationStatus[transformedNumber] !== undefined) {
              // To prevent unnecessary BE calls
              return new Promise(resolve => resolve(state.prepaidNumberValidationStatus[transformedNumber]));
            } else {
              return fetch(`/api/phonenumbervalidator?mdn=${transformedNumber}`)
                .then(async res => {
                  const { data } = await res.json();
                  state.setPrepaidNumberValidationStatus({ [transformedNumber]: data.validNumber === 'true' });
                  return data.validNumber === 'true';
                })
                .catch(err => {
                  console.error(err);
                  return true; // still let user submit the form - Astra should fail transaction if not a prepaid number
                });
            }
          })
        : schema
    ),
  ordCardPin: yup
    .string()
    .required()
    .matches(/^([0-9]){16}$/),
});

function PrepaidCardTopup() {
  const [response, setResponse] = useState({});
  const [isFormValidating, setIsFormValidating] = useState(false);
  const [prepaidNumberValidationStatus, setPrepaidNumberValidationStatus] = useState({});
  const [tooltipText, setTooltipText] = useState('');

  const resolver = useYupValidationResolver(cardTopupValidationSchema, setIsFormValidating);

  let methods = useForm({
    validationResolver: resolver,
    validationContext: {
      state: {
        prepaidNumberValidationStatus,
        setPrepaidNumberValidationStatus,
      },
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
    submitFocusError: true,
  });

  const recaptchaRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    register({ name: 'captchaToken' }, { required: true });
  });

  useEffect(() => {
    // just a workaround to ensure that boundary element in the tooltip popperOptions is already in the DOM
    // TO-DO: find a better solution
    setTooltipText('Enter the 16-digit PIN located on the back of your noSTRINGS Prepaid card or ePIN receipt.');
  }, []);

  const { errors, setValue, register, triggerValidation } = { ...methods };

  const handleClick = async () => {
    const isValid = await triggerValidation();
    if (isValid) {
      recaptchaRef.current.execute();
    } else {
      // still try to submit the form since RHF won't allow that but nevertheless will focus into the first invalid field
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true }));
    }
  };

  const onSubmit = data => {
    trackPromise(
      fetch('/api/redeem-voucher', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          ordPhoneNumber: data.ordPhoneNumber.replace(/[^0-9]/g, ''),
        }),
      })
        .then(response => response.json())
        .then(data => {
          setResponse(data);
        })
        .catch(error => {
          console.error(`redeemVoucher failure - ${error}`);
          setResponse({
            header: {
              success: 'false',
              errorMessage: 'System error',
            },
          });
        })
    );
  };

  const onCapthcaChange = token => {
    setValue('captchaToken', token);
    if (token) {
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true }));
    }
  };

  return (
    <main className="flex-auto">
      <Helmet
        htmlAttributes={{
          lang: 'en',
        }}
      >
        <title>SaskTel.com | noSTRINGS Prepaid Wireless card/ePIN Top‑up Form</title>
      </Helmet>
      {Object.keys(response).length === 0 && (
        <div className="flex flex-col font-museo font-w-300">
          <div className="flex-shrink px-15 md:px-30 lg:px-45 mx-auto my-5 md:my-0 max-w-w-7xl w-full">
            <Breadcrumb
              breadcrumbs={[
                {
                  label: 'Home',
                  link: 'https://www.sasktel.com/wps/wcm/connect/content/home/',
                },
                {
                  label: 'Wireless',
                  link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/',
                },
                {
                  label: 'noStrings Prepaid',
                  link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/prepaid/',
                },
              ]}
            />
            <H1>noSTRINGS Prepaid Wireless card/ePIN Top‑up</H1>
          </div>
          <div className="flex-auto border-t-1 border-light-grey">
            <section className="pt-30 pb-50 px-15 md:py-50 md:px-30 lg:px-0 max-w-w-3xl mx-auto">
              <FormContext {...methods} isFormValidating={isFormValidating}>
                <form onSubmit={methods.handleSubmit(onSubmit)} ref={formRef} classes="flex flex-col" id="form">
                  <Fieldset custom classes="flex flex-col">
                    <div className="px-5 md:px-0 pb-30 w-auto large-12 columns">
                      <div className="font-museo font-w-300 leading-22 text-17">
                        Buy a noSTRINGS Prepaid top-up card or ePIN from one of our many retailers accross Saskatchewan and redeem online below.
                      </div>
                    </div>
                  </Fieldset>
                  <Fieldset custom classes="flex flex-col">
                    <Legend>Top-up Information</Legend>
                    <div className="w-auto m-0 p-0">
                      <GridContainer col="6" gap="30" mdcol="12" mdgap="30">
                        <Input
                          type="tel"
                          placeholder="555-555-5555"
                          spacingClasses="col-span-6 pb-30"
                          label="Wireless phone number"
                          for="ordPhoneNumber"
                          error={errors.ordPhoneNumber}
                        />
                      </GridContainer>
                    </div>
                    <div className="w-auto m-0 p-0">
                      <GridContainer col="6" gap="30" mdcol="12" mdgap="30">
                        <Input
                          spacingClasses="col-span-6 pb-30"
                          label="16-digit pin"
                          for="ordCardPin"
                          tooltip={tooltipText}
                          tooltipName="card-pin-tooltip"
                          error={errors.ordCardPin}
                          inputMode="numeric"
                        />
                      </GridContainer>
                    </div>
                  </Fieldset>
                  <Fieldset classes="px-5 md:px-0 pt-0 pb-30 md:pb-45">
                    <Captcha theme="light" ref={recaptchaRef} sitekey={SITE_KEY} size={'invisible'} badge={'inline'} onChange={onCapthcaChange} />
                  </Fieldset>
                  <Fieldset custom classes="pt-30 md:pt-45 flex flex-col">
                    <div className="w-auto m-0 p-0 flex flex-row">
                      <SubmitButton value="Submit" type="button" name="submit" buttonType="primary" classes="m-auto" click={handleClick}></SubmitButton>
                    </div>
                  </Fieldset>
                </form>
              </FormContext>
            </section>
          </div>
        </div>
      )}
      <div className="flex flex-col font-museo font-w-300" role="status">
        {Object.keys(response).length > 0 && response.header.success === 'true' && (
          <Response
            isApproved={true}
            breadCrumb={[
              {
                label: 'Home',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/',
              },
              {
                label: 'Wireless',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/',
              },
              {
                label: 'noStrings Prepaid',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/prepaid/',
              },
            ]}
            header="Top-up Successful"
            buttonValue="Back"
            buttonOnClick={() => window.history.back()}
          >
            <div className="font-museo font-w-300 leading-22 text-17 mb-50">Your top-up has been successfully applied to your noSTRINGS Prepaid account.</div>
            {(response.data?.externalTransactionId || response.data?.webMethodsTransactionId) && (
              <TransactionField label="Transaction ID" content={response.data.externalTransactionId || response.data.webMethodsTransactionId} sensitive />
            )}
            {response.data?.amount && <TransactionField label="Top-up amount" content={`$${parseFloat(response.data.amount).toFixed(2)}`} />}
            {response.data?.mobileDirectoryNumber && (
              <TransactionField label="Applied to SaskTel prepaid phone number" content={response.data.mobileDirectoryNumber} sensitive />
            )}
            {response.data?.voucherNumber && <TransactionField label="PIN" content={response.data.voucherNumber} sensitive />}
            {response.data?.responseTimeStamp && (
              <TransactionField
                label="Transaction date"
                content={`${new Date(response.data.responseTimeStamp).toLocaleDateString()} ${new Date(response.data.responseTimeStamp).toLocaleTimeString()}`}
              />
            )}
          </Response>
        )}
        {Object.keys(response).length > 0 && response.header.success === 'false' && (
          <Response
            isApproved={false}
            breadCrumb={[
              {
                label: 'Home',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/',
              },
              {
                label: 'Wireless',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/',
              },
              {
                label: 'noStrings Prepaid',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/prepaid/',
              },
            ]}
            header="Top-up Could Not Be Applied"
            buttonValue="Back"
            buttonOnClick={() => window.history.back()}
          >
            <div className="w-auto m-0 p-0 mb-50 text-center">
              <p className="mb-15 md:mb-20">
                {response.header.errorMessage === '' ? 'Your top-up could not be applied to your noSTRINGS Prepaid account.' : 'There was a system error.'}
              </p>
              <p className="mb-0">
                {response.header.errorMessage === ''
                  ? 'Please make sure you entered your phone number and PIN correctly. If you continue to experience issues, please call'
                  : 'Please try again later. If you continue to experience issues, please call'}{' '}
                <a
                  href="tel:18007275835"
                  className="font-museo font-w-600 text-primary duration-500 focus:text-almost-black hover:text-almost-black focus:underline hover:underline"
                >
                  1.800.SASKTEL (1.800.727.5835)
                </a>
                .
              </p>
            </div>
            {response.data?.responseTimeStamp && (
              <TransactionField
                label="Transaction date"
                content={`${new Date(response.data.responseTimeStamp).toLocaleDateString()} ${new Date(response.data.responseTimeStamp).toLocaleTimeString()}`}
              />
            )}
          </Response>
        )}
      </div>
    </main>
  );
}

export default PrepaidCardTopup;
